import React, { useState, type FC, type PropsWithChildren, useContext } from 'react';

import { getI18nData } from '@lucid/utils';

import type { Lucid } from '../../types/types';
import Nav, { type SharedNavOptions, type ContextUseStateType } from '../../components/nav/nav';
import NavSkipButton from '../../components/nav/nav-skip-button';
import type { ComponentImage } from '../../types/generics';
import LayoutContext from '../../components/context/layout-context';

export interface NavSectionOptions extends SharedNavOptions {
  /** Renders the top-level nav if enabled. */
  hasGlobalNav?: boolean;
  /** The component variants. */
  variants?: {
    logo?: Lucid.Theme.Component.Nav.Logo.VariantKeys;
  };
}

export interface NavProps {
  /** The top level Navigation buttons. */
  buttons?: Lucid.Directus.Generated.Menu_Link[];
  /** The Nav level Navigation items. */
  items?: Lucid.Directus.Generated.Nav_Section_Translations[];
  /** The Logo object containing alt and src keys. */
  logo?: ComponentImage;
  /** The link that wraps the Logo. */
  logoLink?: string;
}

export interface NavWrapperProps extends NavProps, NavSectionOptions {
  /** Whether this is the global nav */
  isGlobalNav?: boolean;
  /** The page translations for the LanguageSwitcher */
  nodeTranslations?: Lucid.Directus.Custom.NodeTranslation[];
  /** The i18n translation data. */
  i18nData: Lucid.I18n.DataEdges;
  /** The text for the view more link. */
  viewMoreText?: string;
  /** The aria label for screen readers */
  buttonAriaLabel: string;
}

export interface NavSectionProps extends NavWrapperProps {
  /** The props passed into the global Nav */
  globalNav?: NavProps;
  /** The props passed into the site Nav */
  siteNav: NavProps;
}

const lucidSuiteLogo = {
  imageSrc: 'https://cdn-cashy-static-assets.lucidchart.com/lucid/logos/lucid.svg',
  imageAlt: 'Lucid Logo',
};

const NavWrapper: FC<PropsWithChildren<NavWrapperProps>> = ({
  isGlobalNav,
  buttons,
  items,
  logo,
  logoLink,
  nodeTranslations,
  buttonAriaLabel,
  i18nData,
  hasGlobalNav,
  viewMoreText,
  variants,
}) => {
  const { shortCode } = useContext(LayoutContext);
  const i18n = getI18nData(i18nData, shortCode);

  return (
    <Nav
      aria-label={i18n(isGlobalNav ? 'mktg-global-nav-aria-label' : 'mktg-nav-aria-label')}
      isGlobalNav={isGlobalNav}
      viewMoreText={viewMoreText}
    >
      <Nav.Logo logo={logo || lucidSuiteLogo} logoLink={logoLink || 'https://lucid.co'} variant={variants?.logo} />
      <Nav.Menu
        buttonAriaLabel={buttonAriaLabel}
        buttons={buttons || []}
        hasGlobalNav={hasGlobalNav}
        i18nData={i18nData}
        isGlobalNav={isGlobalNav}
        items={items || []}
        nodeTranslations={nodeTranslations}
      />
    </Nav>
  );
};

const NavSection: FC<PropsWithChildren<NavSectionProps>> = ({ hasGlobalNav = false, siteNav, globalNav, ...rest }) => {
  const [[activeMenuCallback], setActiveMenuCallback] = useState<ContextUseStateType>([]);

  const handleActiveMenu = (currentActiveMenu: ContextUseStateType) => {
    const [specificCurrentActiveMenu] = currentActiveMenu;

    if (!activeMenuCallback) {
      return setActiveMenuCallback(currentActiveMenu);
    }

    if (activeMenuCallback !== specificCurrentActiveMenu) {
      activeMenuCallback(false);

      return setActiveMenuCallback(currentActiveMenu);
    }
  };

  const context = {
    setActiveMenuCallback: handleActiveMenu,
  };

  return (
    <Nav.SiteContext.Provider value={context}>
      <NavSkipButton />
      {hasGlobalNav && globalNav && <NavWrapper {...rest} {...globalNav} isGlobalNav />}
      <NavWrapper {...rest} {...siteNav} hasGlobalNav={hasGlobalNav} />
    </Nav.SiteContext.Provider>
  );
};

export { NavSection };
export default NavSection;
